import {FetchMethod} from "@web-libs/core/http/infrastructure/shared/shared.types";
import {HttpHeaders, RequestOptions} from "@web-libs/core/http/api/http.types";
import {HttpClient} from "@web-libs/core/http";
import {withHeaders, withTenantId} from "@web-libs/core/http/api/http.functions";

/**
 * Creates a HttpClient that uses the provided fetchMethod to make requests
 * @param baseUrl - the base url to use for all requests
 * @param fetchMethod - the fetch method to use for all requests
 * @param defaultHeaders - the default headers to use for all requests (will be added to RequestOptions)
 * @param tenantId - the tenantId to use for all requests (will be added to RequestOptions)
 */



export function createHttpClient(baseUrl: string, fetchMethod: FetchMethod, defaultHeaders?: HttpHeaders, tenantId?: string, lang?: string): HttpClient {
    const _fetchWithHeadersAndTenantId: FetchMethod = async (method: string, url: string, body?: any, requestOptions?: RequestOptions) => {
        const defaultHeadersWithTenant = tenantId && !(requestOptions?.skipTenantId) ? {
            ...defaultHeaders,
            'X-TenantID': tenantId
        } : defaultHeaders;
        const optionsWithDefaultHeaders = withHeaders(requestOptions, defaultHeadersWithTenant);
        const optionsWithBoth = withTenantId(optionsWithDefaultHeaders, tenantId);
        const optionsWithLang: RequestOptions = !requestOptions?.skipLang ? {
            ...optionsWithBoth,
            queryParams: {
               ...optionsWithBoth.queryParams,
               lang: lang ?? 'de'
            }
        } : optionsWithBoth
        return await fetchMethod(method, url, body, optionsWithLang)
    }

    return _createHttpClient(baseUrl, _fetchWithHeadersAndTenantId)
}

function _createHttpClient(baseUrl: string, fetchMethod: FetchMethod): HttpClient {
    return {
        baseUrl,
        get: _createSimpleRequest('GET', fetchMethod),
        post: _createBodyRequest('POST', fetchMethod),
        put: _createBodyRequest('PUT', fetchMethod),
        delete: _createSimpleRequest('DELETE', fetchMethod),
        postForm: _createFormRequest('POST', fetchMethod),
        postFormData: _createFormDataRequest('POST', fetchMethod),
        getBlob: _createBlobRequest('GET', fetchMethod)
    }
}

const _createSimpleRequest = (method: string, fetchMethod: FetchMethod) =>
    async (url: string, requestOptions?: RequestOptions) => {
        return await fetchMethod(method, url, undefined, requestOptions)
    }

const _createBodyRequest = (method: string, fetchMethod: FetchMethod) =>
    async (url: string, body: any, requestOptions?: RequestOptions) => {
        const optionsWithHeaders = withHeaders(requestOptions, {'Content-Type': 'application/json'})
        return await fetchMethod(method, url, body, optionsWithHeaders)
    }

const _createFormRequest = (method: string, fetchMethod: FetchMethod) =>
    async (url: string, body: URLSearchParams, requestOptions?: RequestOptions) => {
        const urlEncodedHeaders = {'Content-Type': 'application/x-www-form-urlencoded'}
        const urlEncodedData = body.toString();
        const requestOptionsWithUrlEncodedHeaders = withHeaders(requestOptions, urlEncodedHeaders);
        return await fetchMethod(method, url, urlEncodedData, requestOptionsWithUrlEncodedHeaders)
    }

const _createFormDataRequest = (method: string, fetchMethod: FetchMethod) =>
    async (url: string, body: FormData, requestOptions?: RequestOptions) => {
        const urlEncodedHeaders =  {} //{'Content-Type': 'multipart/form-data'}
        const requestOptionsWithFormDataHeaders =  withHeaders(requestOptions, urlEncodedHeaders);
        delete requestOptionsWithFormDataHeaders.headers?.['Content-Type'];
        return await fetchMethod(method, url, body, requestOptionsWithFormDataHeaders)
    }

const _createBlobRequest = (method: string, fetchMethod: FetchMethod) =>
async (url: string, requestOptions?: RequestOptions) => {
    const optionsWithHeaders = withHeaders(requestOptions, {'Content-Type': 'application/octet-stream'})
    return await fetchMethod(method, url, undefined, optionsWithHeaders)
}
